import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RadioGroup, Text } from "shared/components/Spor";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { getValueFromStopRef } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleCancellation";
import { FC, FormEvent, useState } from "react";
import {
  AlternativeTransportCancelReq,
  AlternativeTransportItineraryStop,
  AlternativeTransportVehicle,
} from "shared/types/alternativeTransport";
import styled from "styled-components";
import { sliceList } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/utils";
import { ActionModal } from "shared/components/ActionModal";
import { RadioButton } from "shared/components/forms/RadioButton";
import { Select } from "shared/components/forms/Select";
import { trimStationName } from "features/VehicleSidebar/VehicleList/utils/stopNameUtils";

type RadioOptions = "PARTIALLY" | "FULLY";

const RadioGroupStyled = styled(RadioGroup)`
  display: flex;
  gap: 24px;
`;

const ChooseItinerarySection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  width: 100%;
  margin: 18px 0 24px;
`;

type ModalProps = {
  vehicle: AlternativeTransportVehicle;
  itineraryStops: AlternativeTransportItineraryStop[];
  firstStopId: string;
  lastStopId: string;
  setShowModal: (show: boolean) => void;
};

export const VehicleCancellationModal: FC<ModalProps> = ({
  vehicle,
  itineraryStops,
  firstStopId,
  lastStopId,
  setShowModal,
}) => {
  const [selectedRadio, setSelectedRadio] = useState<RadioOptions>("PARTIALLY");
  const [fromStopId, setFromStopId] = useState<string>(firstStopId);
  const [toStopId, setToStopId] = useState<string>(lastStopId);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: AlternativeTransportCancelReq) =>
      axiosClient.patch<any>(
        `${getBackendUrl()}/alternativeTransports/${vehicle.id}/cancel`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["altTransDetails"] });
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let stopsToCancel: AlternativeTransportItineraryStop[];

    if (selectedRadio === "FULLY") {
      stopsToCancel = itineraryStops;
    } else {
      stopsToCancel = sliceList({
        list: itineraryStops,
        fromStopId,
        toStopId,
      });
    }

    mutation.mutate({
      cancelledStops: stopsToCancel.map((stop) => ({
        stopReference: stop.stopReference,
        stopName: stop.name,
        isArrivalCancelled: true,
        isDepartureCancelled: true,
        stopActivity: stop.activity,
      })),
    });
  };

  const onClose = () => {
    setShowModal(false);
    mutation.reset();
  };

  const stopSelectionFromStop = sliceList({ list: itineraryStops, toStopId });
  const stopSelectionToStop = sliceList({ list: itineraryStops, fromStopId });
  /* For the select input */
  const toSelectItem = (stop: AlternativeTransportItineraryStop) => ({
    text: stop.name ?? "",
    value: getValueFromStopRef(stop.stopReference),
  });
  const stopSelectionFromStopItems = stopSelectionFromStop.map(toSelectItem);
  const stopSelectionToStopItems = stopSelectionToStop.map(toSelectItem);

  return (
    <ActionModal
      title={`Kanseller avgang ${vehicle.workShiftPlan?.shiftId}`}
      actionTitle="Gjennomfør"
      onClose={onClose}
      onSubmit={onSubmit}
      loading={mutation.isPending}
      isSuccess={mutation.isSuccess}
      isError={mutation.isError}
      successMessage="Kansellering vellykket."
      failureMessage="Kunne ikke kansellere avgang. Prøv igjen, eller kontakt IT hvis feilen vedvarer."
    >
      <RadioGroupStyled role="radiogroup" defaultValue="delvis" width="100%">
        <RadioButton
          value="delvis"
          onChange={() => setSelectedRadio("PARTIALLY")}
        >
          Kanseller delvis
        </RadioButton>
        <RadioButton value="helt" onChange={() => setSelectedRadio("FULLY")}>
          Kanseller helt
        </RadioButton>
      </RadioGroupStyled>

      <ChooseItinerarySection>
        {selectedRadio === "PARTIALLY" ? (
          <>
            <Select
              width="100%"
              label="Fra stopp"
              aria-label="Velg fra stopp"
              value={fromStopId}
              onChange={(e) => setFromStopId(e.target.value)}
            >
              {stopSelectionFromStopItems.map((item) => (
                <option key={item.value} value={item.value}>
                  {trimStationName(item.text)}
                </option>
              ))}
            </Select>
            <Select
              width="100%"
              label="Til stopp"
              aria-label="Velg til stopp"
              value={toStopId}
              onChange={(e) => setToStopId(e.target.value)}
            >
              {stopSelectionToStopItems.map((item) => (
                <option key={item.value} value={item.value}>
                  {trimStationName(item.text)}
                </option>
              ))}
            </Select>
          </>
        ) : (
          <>
            <Text>{trimStationName(vehicle.originStopName)}</Text>-
            <Text>{trimStationName(vehicle.destinationStopName)}</Text>
          </>
        )}
      </ChooseItinerarySection>
    </ActionModal>
  );
};
